// storytell-ai-platform/pkg/ts/core/src/screens/drawer/StoryTileDrawerScreen.tsx
import { stAnalytics } from "@repo/analytics";
import type { curator, assets as assetsNS } from "@repo/client";
import { getRequestClient, codes } from "@repo/client";
import { type Component, Show, createMemo, createSignal, createResource, createEffect, Switch, Match } from "solid-js";
import { Dynamic } from "solid-js/web";
import { classNames } from "@core/lib/classNames";
import { BlockQuote } from "@core/components/BlockQuote";
import { Anchor } from "@core/components/cta/Anchor";
import { PrimaryCTA } from "@core/components/cta/PrimaryCTA";
import { SecondaryCTA } from "@core/components/cta/SecondaryCTA";
import { getScreenType } from "@core/lib/ui/getScreenType";
import { originalFilenameToMimeType } from "@core/lib/originalFilenameToMimeType";
import ownStyles from "./StoryTileDrawerScreen.module.css";
import { CollectionAssetsTable } from "@core/components/CollectionAssetTable";
import { useWire } from "@core/wire";
import { formatCollectionName } from "@core/lib/ui/formatCollectionName";
import { Spinner } from "@core/components/_original/Spinner";
import { readableProcessingState, ProcessingReadableState } from "@core/lib/ui/readableProcessingState";
import { StIcon } from "@core/components/icons";
import { TbEye } from "solid-icons/tb";
import { useFileTypeIcon, type FileTypeIcon } from "@core/ui/useFileTypeIcon";

export const StoryTileDrawerScreen: Component<{
  tiles: Array<curator.Citation & { displayNumber: number }>;
  initial: string;
  assetRefMap?: curator.AssetRefMap | { [key: string]: { key: string; value: curator.AssetReference } };
}> = (props) => {
  const wire = useWire();
  const client = getRequestClient(wire.services.auth.token);
  const tiles = createMemo(() => props.tiles);

  const normalizedAssetRefMap = createMemo(() => {
    const currentPAssetRefMap = props.assetRefMap; // Explicitly depend on props.assetRefMap

    if (!currentPAssetRefMap) {
      return undefined;
    }
    const firstKey = Object.keys(currentPAssetRefMap)[0];
    const firstEntry = firstKey ? (currentPAssetRefMap as Record<string, unknown>)[firstKey] : undefined;
    if (
      firstKey &&
      !Number.isNaN(Number(firstKey)) &&
      typeof firstEntry === "object" &&
      firstEntry !== null &&
      "key" in firstEntry &&
      "value" in firstEntry
    ) {
      const newMap: curator.AssetRefMap = {};
      Object.values(currentPAssetRefMap as { [key: string]: { key: string; value: curator.AssetReference } }).forEach(
        (entry) => {
          if (entry && typeof entry.key === "string" && entry.value) newMap[entry.key] = entry.value;
        },
      );
      return newMap;
    }
    return currentPAssetRefMap as curator.AssetRefMap;
  });

  const active = () => tiles().find((a) => a.citationId === props.initial);
  const [current, setCurrent] = createSignal<(curator.Citation & { displayNumber: number }) | undefined>(active());
  const max = () => tiles().length - 1;
  const [position, setPosition] = createSignal<number>(tiles().findIndex((c) => c.citationId === props.initial));

  // Effect to update current tile and position when props.initial or props.tiles change
  createEffect(() => {
    const initialId = props.initial;
    const currentTiles = tiles(); // tiles() is a memo derived from props.tiles

    const newIdx = currentTiles.findIndex((c) => c.citationId === initialId);
    setPosition(newIdx); // Will be -1 if not found

    const newCurrentTile = currentTiles.find((a) => a.citationId === initialId);
    setCurrent(newCurrentTile); // Will be undefined if not found
  });

  const currentCuratorAssetRef = createMemo(() => {
    const refId = current()?.assetReferenceId;
    const map = normalizedAssetRefMap();

    if (!refId || !map) {
      return undefined;
    }

    const result = map[refId];
    return result;
  });

  // Resource to fetch asset state
  const [assetStateResource] = createResource(
    () => currentCuratorAssetRef()?.assetId,
    async (targetAssetId) => {
      if (!targetAssetId) {
        return undefined;
      }
      try {
        const response = await client.controlplane.GetAssetByID(targetAssetId);
        if (response.code === codes.OK && response.data) {
          return response.data as assetsNS.AssetState;
        }
        return undefined;
      } catch (error) {
        return undefined;
      }
    },
  );

  const formatText = (text: string) => {
    const applyPrefix = /^[a-z]/.test(text);
    const applySuffix = !text.endsWith(".") && !text.endsWith("!");
    return `${applyPrefix ? "..." : ""} ${text}${applySuffix ? "..." : ""}`;
  };

  const next = (event: Event) => {
    event.preventDefault();
    if (position() !== max()) {
      const newPosition = position() + 1;
      setPosition(newPosition);
      setCurrent(tiles()[newPosition]);
    }
    stAnalytics.track("click_tracking", {
      cta: "story_tile_navigate",
      position: "right_drawer",
      screen: getScreenType(),
    });
  };

  const previous = (event: Event) => {
    event.preventDefault();
    if (position() !== 0) {
      const newPosition = position() - 1;
      setPosition(newPosition);
      setCurrent(tiles()[newPosition]);
    }
    stAnalytics.track("click_tracking", {
      cta: "story_tile_navigate",
      position: "right_drawer",
      screen: getScreenType(),
    });
  };

  return (
    <div class={ownStyles["story-tile-drawer"]}>
      {/* Top navigation and title */}
      <Show when={tiles().length > 1}>
        <div class={ownStyles["story-tile-drawer__top"]}>
          <div class={ownStyles["story-tile-drawer__navigation"]}>
            <div class={ownStyles["nav-button-wrapper"]}>
              <SecondaryCTA
                onClick={previous}
                label="Previous"
                accessiblePrefix="View "
                accessibleSuffix="citation."
                data-test-id="previous-citation"
                inactive={position() === 0}
              />
            </div>
            <div class={ownStyles["nav-button-wrapper"]}>
              <PrimaryCTA
                onClick={next}
                label="Next"
                accessiblePrefix="View "
                accessibleSuffix="citation."
                data-test-id="next-citation"
                inactive={position() === max()}
              />
            </div>
          </div>
          <p class={ownStyles["story-tile-drawer__count-container"]}>
            <span
              class={ownStyles["story-tile-drawer__title-small"]}
            >{`Citation ${current() ? `#${current()!.displayNumber}` : ""}`}</span>
            <span class={ownStyles["story-tile-drawer__count"]}>{position() + 1}</span>
            <span class={ownStyles["story-tile-drawer__count"]}>of</span>
            <span class={ownStyles["story-tile-drawer__count"]}>{max() + 1}</span>
          </p>
        </div>
      </Show>
      <Show when={tiles().length === 1}>
        <p
          class={ownStyles["story-tile-drawer__title"]}
        >{`Citation ${current() ? `#${current()!.displayNumber}` : ""}`}</p>
      </Show>

      <Show when={current()} fallback={<p>No current citation selected.</p>}>
        <p class={ownStyles["story-tile-drawer__subtitle"]}>Citation extracted from your asset</p>
        <p class={ownStyles["story-tile-drawer__formatting-note"]}>Format may differ from the original content</p>
        <BlockQuote class={ownStyles["story-tile-drawer__extract"]}>
          <p>{formatText(current()?.text || "")}</p>
        </BlockQuote>

        <div class={ownStyles["story-tile-drawer__subtitle"]}>Citation from asset</div>

        <Show when={current()?.assetReferenceId}>
          {/* Check if we have asset reference data before attempting to load */}
          <Show when={!currentCuratorAssetRef()}>
            <p class={ownStyles["story-tile-drawer__asset-error"]}>
              {(() => {
                const refId = current()?.assetReferenceId;
                const map = normalizedAssetRefMap();
                const mapKeys = map ? Object.keys(map) : [];

                if (!map || mapKeys.length === 0) {
                  return "Asset reference data is not available.";
                }
                if (refId && !mapKeys.includes(refId)) {
                  return `Asset reference ID "${refId}" not found in available references.`;
                }
                return `Asset reference lookup failed for ID: ${refId}`;
              })()}
            </p>
          </Show>

          {/* Only show asset loading/display when we have a valid asset reference */}
          <Show when={currentCuratorAssetRef()}>
            <Show
              when={assetStateResource.state === "ready"}
              fallback={
                <>
                  <Show when={assetStateResource.loading}>
                    {" "}
                    {/* .loading is true for pending/refreshing */}
                    <div class={ownStyles["story-tile-drawer__asset-loading"]}>
                      {" "}
                      <Spinner />{" "}
                    </div>
                  </Show>
                  <Show when={assetStateResource.error}>
                    {" "}
                    {/* Resource has an error property */}
                    <p class={ownStyles["story-tile-drawer__asset-error"]}>
                      {" "}
                      Could not load details for asset: {currentCuratorAssetRef()?.displayName || "N/A"}. (Error:{" "}
                      {String(assetStateResource.error)}){" "}
                    </p>
                  </Show>
                  {/* Fallback for states that are not 'ready', not 'loading', and not 'error' */}
                  <Show
                    when={
                      !assetStateResource.loading && !assetStateResource.error && assetStateResource.state !== "ready"
                    }
                  >
                    <p class={ownStyles["story-tile-drawer__asset-error"]}>
                      Asset status is indeterminate. Current state: {assetStateResource.state}.
                    </p>
                  </Show>
                </>
              }
            >
              {(() => {
                // This block only runs if assetStateResource.state === "ready"
                const assetFromResource = assetStateResource(); // Get the actual data

                if (!assetFromResource) {
                  return (
                    <p class={ownStyles["story-tile-drawer__asset-error"]}>
                      Asset data is unexpectedly unavailable. Resource reported ready but yielded no data.
                    </p>
                  );
                }

                const asset = assetFromResource as assetsNS.AssetState & {
                  modifiedAt?: string;
                  collectionIds?: string[];
                };

                const iconTheme = useFileTypeIcon(originalFilenameToMimeType(asset.originalFileName) as FileTypeIcon);
                const status = readableProcessingState(asset.lifecycleState, asset.modifiedAt);

                const breadcrumbCollectionId = asset.collectionIds?.[0] || "";
                const sourceCollection = createMemo(() => {
                  if (!breadcrumbCollectionId) return undefined;

                  if (!wire.services.collections || typeof wire.services.collections.getCollection !== "function") {
                    return undefined;
                  }

                  const collectionAccessor = wire.services.collections.getCollection(breadcrumbCollectionId);
                  if (typeof collectionAccessor !== "function") {
                    return undefined;
                  }
                  return (collectionAccessor as () => { label: string } | undefined)();
                });

                const assetDataForTable = {
                  id: asset.id,
                  assetId: asset.id,
                  displayName: asset.displayName,
                  originalFilename: asset.originalFileName,
                  contentType: asset.contentType,
                  lifecycleState: asset.lifecycleState,
                  modifiedAt: asset.modifiedAt,
                  signedURL: asset.signedURL,
                  summary: asset.summary || "",
                };

                const assetEntity = {
                  data: assetDataForTable as unknown as assetsNS.AssetSnapshot,
                  collectionIds: breadcrumbCollectionId ? [breadcrumbCollectionId] : [],
                  flags: [] as curator.AssetAssocFlags[],
                  actions: [] as string[],
                };

                return (
                  <CollectionAssetsTable
                    modifier="drawer"
                    collectionName={sourceCollection()?.label || formatCollectionName("Source Asset")}
                    collectionId={breadcrumbCollectionId}
                    data={[assetEntity]}
                  />
                );
              })()}
            </Show>
          </Show>
        </Show>

        <Show when={!current()?.assetReferenceId}>
          <p class={ownStyles["story-tile-drawer__asset-error"]}>No asset reference ID for this citation.</p>
        </Show>
      </Show>
      <p class={ownStyles["story-tile-drawer__docs"]}>
        <Anchor
          data-test-id="storytile-docs-link"
          accessibleSuffix="Visit our documentation to"
          label="Learn about the importance of Citations"
          href="https://docs.storytell.ai/under-the-hood/citations"
          target="_blank"
        />
      </p>
    </div>
  );
};
